/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getOr } from 'lodash/fp';
import moment from 'moment';
import api from './api';

const name = 'loyalty';

const getNewLoyaltyProgram = createAsyncThunk(
  'loyalty/getNewLoyaltyProgram',
  async () => {
    const response = await api.getNewLoyaltyProgram();
    return response;
  }
);

const getProgramList = createAsyncThunk(
  'loyalty/getProgramList',
  async (payload) => {
    const response = await api.getProgramList(payload);
    return response;
  }
);
const getOverarchingProgramList = createAsyncThunk(
  'loyalty/getOverarchingProgramList',
  async (limit) => {
    const response = await api.getOverarchingProgramList(limit);
    return response;
  }
);

const enableOverarchingProgram = createAsyncThunk(
  'loyalty/enableOverarchingProgram',
  async (payload) => {
    const response = await api.enableOverarchingProgram(payload);
    return response;
  }
);

const getLoyaltyList = createAsyncThunk(
  'loyalty/getLoyaltyList',
  async (limit) => {
    const response = await api.getLoyaltyList(limit);
    return response;
  }
);

const getItems = createAsyncThunk('loyalty/getItems', async (limit) => {
  const response = await api.getItems(limit);
  return response;
});

const getItemsByCategory = createAsyncThunk(
  'loyalty/getItemsByCategory',
  async (categories) => {
    const response = await api.getItemsByCategory(categories);
    return response;
  }
);

const downloadLoyaltyCSV = createAsyncThunk(
  'loyalty/downloadLoyaltyCSV',
  async () => {
    const response = await api.getLoyaltyList({ limit: 2500 });
    return response;
  }
);

const uploadLoyaltyCSV = createAsyncThunk(
  'loyalty/uploadLoyaltyCSV',
  async (file) => {
    const response = await api.uploadLoyaltyCSV(file);
    return response;
  }
);

const saveLoyaltyProgram = createAsyncThunk(
  'loyalty/saveLoyaltyProgram',
  async (payload) => {
    const response = await api.saveLoyaltyProgram(payload);
    return response;
  }
);

const editLoyaltyProgram = createAsyncThunk(
  'loyalty/editLoyaltyProgram',
  async (payload) => {
    const response = await api.saveLoyaltyProgram(payload);
    return response;
  }
);

const setEditProgram = createAsyncThunk(
  'loyalty/setEditProgram',
  async (payload) => {
    return payload;
  }
);

const getLoyaltyTransactions = createAsyncThunk(
  'loyalty/getLoyaltyTransactions',
  async (params) => {
    const response = await api.getLoyaltyTransactions(params);
    if (response?.data) {
      response.data = response.data.map((p) => {
        switch (p.type) {
          case 0:
            p.pointsEarned = p.points;
            break;
          case 1:
            p.pointsRedeemed = p.points;
            break;
          default:
            p.pointsEarned = p.points;
            break;
        }
        return p;
      });
    }
    return response;
  }
);

const resendEmail = createAsyncThunk('loyalty/resendEmail', async (params) => {
  const response = await api.resendEmail(params);
  return response;
});
const sendReferralEmail = createAsyncThunk(
  'loyalty/sendReferralEmail',
  async (params) => {
    const response = await api.sendReferralEmail(params);
    return response;
  }
);
const sendWhatsApp = createAsyncThunk(
  'loyalty/sendWhatsApp',
  async (params) => {
    const response = await api.sendWhatsApp(params);
    return response;
  }
);

const getLoyaltyByProgram = createAsyncThunk(
  'loyalty/customers-by-program',
  async (params) => {
    const response = await api.getLoyaltyProgramApi(params);
    return response;
  }
);

const sendCustomEmail = createAsyncThunk(
  'loyalty/send-custom-email',
  async (params) => {
    const response = await api.sendCustomEmailApi(params);
    return response;
  }
);

const dateFormat = (row) =>
  row ? moment(`${row}Z`).format('YYYY-MM-DD HH:mm:ss') : '';

const getTopCustomerEarnPoint = createAsyncThunk(
  'loyalty/get-loyalty-earn-points',
  async (params) => {
    const response = await api.getTopCustomerEarnPointt(params);
    return response;
  }
);
const getReferredCustomers = createAsyncThunk(
  'loyalty/getReferredCustomers',
  async (params) => {
    const response = await api.getReferredCustomers(params);
    return response;
  }
);

const getSpendStats = createAsyncThunk(
  'loyalty/getSpendStats',
  async (payload) => {
    const response = await api.getSpendStats(payload);
    return response;
  }
);

const getSpendList = createAsyncThunk(
  'loyalty/getSpendList',
  async (payload) => {
    const response = await api.getSpendList(payload);
    return response;
  }
);

const getBusinessSpendList = createAsyncThunk(
  'loyalty/getBusinessSpendList',
  async (payload) => {
    const response = await api.getBusinessSpendList(payload);
    return response;
  }
);

const getLocationSpendList = createAsyncThunk(
  'loyalty/getLocationSpendList',
  async (payload) => {
    const response = await api.getLocationSpendList(payload);
    return response;
  }
);

const changeLoyaltyProgram = createAsyncThunk(
  'loyalty/change-loyalty-program',
  async (params) => {
    const response = await api.changeLoyaltyProgramApi(params);
    return response;
  }
);

const { actions, reducer } = {
  ...createSlice({
    name,
    initialState: {
      program: {},
      editProgram: {},
      isLoading: false,
      imgIsLoading: false,
      items: [],
      checkIn: [],
      programs: [],
      loyalties: [],
      total: 0,
      csv: [],
      error: null,
      loyaltyTransactions: {
        isLoading: false,
        total: 0,
        data: [],
      },
      overarchingPrograms: {
        total: 0,
        data: [],
      },
      isSending: false,
      programsById: [],
      listCustomerEarnPoint: [],
      referredCustomers: [],
      spendStats: [],
      spendList: [],
      secondarySpendList: [],
      locationSpendList: [],
    },
    extraReducers: {
      [getProgramList.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [getProgramList.fulfilled]: (state, action) => ({
        ...state,
        isLoading: false,
        programs: getOr([], 'payload', action),
      }),
      [getProgramList.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [getLoyaltyList.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [getLoyaltyList.fulfilled]: (state, action) => ({
        ...state,
        isLoading: false,
        loyalties: getOr([], 'payload', action),
      }),
      [getLoyaltyList.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [getNewLoyaltyProgram.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [getNewLoyaltyProgram.fulfilled]: (state, action) => ({
        ...state,
        isLoading: false,
        program: getOr({}, 'payload', action),
      }),
      [getNewLoyaltyProgram.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [saveLoyaltyProgram.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [saveLoyaltyProgram.fulfilled]: (state, action) => ({
        ...state,
        isLoading: false,
        program: getOr({}, 'payload', action),
      }),
      [saveLoyaltyProgram.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [setEditProgram.fulfilled]: (state, action) => ({
        ...state,
        editProgram: getOr({}, 'payload', action),
      }),
      [editLoyaltyProgram.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [editLoyaltyProgram.fulfilled]: (state, action) => ({
        ...state,
        isLoading: false,
        editProgram: getOr({}, 'payload', action),
      }),
      [editLoyaltyProgram.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [getItems.pending]: (state) => ({
        ...state,
      }),
      [getItems.fulfilled]: (state, action) => ({
        ...state,
        items: getOr([], 'payload', action),
      }),
      [getItems.rejected]: (state, payload) => ({
        ...state,
        error: payload,
      }),
      [getItemsByCategory.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [getItemsByCategory.fulfilled]: (state) => ({
        ...state,
        isLoading: false,
        // items: getOr([], 'payload', action),
      }),
      [getItemsByCategory.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [uploadLoyaltyCSV.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [uploadLoyaltyCSV.fulfilled]: (state) => ({
        ...state,
        isLoading: false,
      }),
      [uploadLoyaltyCSV.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [downloadLoyaltyCSV.fulfilled]: (state, action) => ({
        ...state,
        isLoading: false,
        csv: getOr([], 'payload.data', action).map((r) => ({
          'Loyalty Program Name': r.programName,
          'Loyalty Number': r.code,
          'First Name': r.firstName,
          'Last Name': r.lastName,
          'Email Address': r.email,
          'Mobile Number': r.phone,
          'Birthday Month': Number(r.birthMonth)
            ? moment()
                .month(Number(r.birthMonth) - 1)
                .format('MMM')
            : '',
          'Current Points': r.point,
          'Total Points Earned': r.totalPoint,
          'Total Rewards Earned': r.rewardedTimes,
          'Total Rewards Redeemed': r.redeemedTimes,
          'Last Points Earned (YYYY-MM-DD HH:MM:SS)': dateFormat(r.lastEarned),
          'Last Reward Earned (YYYY-MM-DD HH:MM:SS)': dateFormat(r.lastReward),
          'Last Reward Redeemed (YYYY-MM-DD HH:MM:SS)': dateFormat(
            r.lastRedeemed
          ),
          'Sign Up Date (YYYY-MM-DD HH:MM:SS)': dateFormat(r.createdAt),
        })),
      }),
      [getLoyaltyTransactions.pending]: (state) => ({
        ...state,
        loyaltyTransactions: { ...state.loyaltyTransactions, isLoading: true },
      }),
      [getLoyaltyTransactions.fulfilled]: (state, action) => ({
        ...state,
        loyaltyTransactions: { ...action.payload, isLoading: false },
      }),
      [getLoyaltyTransactions.rejected]: (state, payload) => ({
        ...state,
        loyaltyTransactions: { ...state.loyaltyTransactions, isLoading: true },
        error: payload,
      }),
      [resendEmail.pending]: (state) => ({
        ...state,
        isSending: true,
      }),
      [resendEmail.fulfilled]: (state) => ({
        ...state,
        isSending: false,
      }),
      [resendEmail.rejected]: (state, payload) => ({
        ...state,
        isSending: false,
        error: payload,
      }),
      [getLoyaltyByProgram.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [getLoyaltyByProgram.fulfilled]: (state, action) => ({
        ...state,
        isLoading: false,
        programsById: getOr([], 'payload', action),
      }),
      [getLoyaltyByProgram.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [enableOverarchingProgram.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [enableOverarchingProgram.fulfilled]: (state) => ({
        ...state,
        isLoading: false,
      }),
      [enableOverarchingProgram.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [getOverarchingProgramList.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [getOverarchingProgramList.fulfilled]: (state, payload) => ({
        ...state,
        isLoading: false,
        overarchingPrograms: getOr({ data: [], total: 0 }, 'payload', payload),
      }),
      [getOverarchingProgramList.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [getTopCustomerEarnPoint.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [getTopCustomerEarnPoint.fulfilled]: (state, payload) => ({
        ...state,
        isLoading: false,
        listCustomerEarnPoint: getOr([], 'payload', payload),
      }),
      [getTopCustomerEarnPoint.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [getReferredCustomers.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [getReferredCustomers.fulfilled]: (state, payload) => ({
        ...state,
        isLoading: false,
        referredCustomers: getOr([], 'payload', payload),
      }),
      [getReferredCustomers.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [getSpendStats.pending]: (state) => ({
        ...state,
        isLoading: true,
        spendStats: [],
      }),
      [getSpendStats.fulfilled]: (state, action) => ({
        ...state,
        isLoading: false,
        spendStats: getOr([], 'payload', action),
      }),
      [getSpendStats.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [getSpendList.pending]: (state) => ({
        ...state,
        isLoading: true,
        spendList: [],
      }),
      [getSpendList.fulfilled]: (state, action) => ({
        ...state,
        isLoading: false,
        spendList: getOr([], 'payload', action),
      }),
      [getSpendList.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [getBusinessSpendList.pending]: (state) => ({
        ...state,
        isLoading: true,
        secondarySpendList: [],
      }),
      [getBusinessSpendList.fulfilled]: (state, action) => ({
        ...state,
        isLoading: false,
        secondarySpendList: getOr([], 'payload', action),
      }),
      [getBusinessSpendList.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [getLocationSpendList.pending]: (state) => ({
        ...state,
        isLoading: true,
        locationSpendList: [],
      }),
      [getLocationSpendList.fulfilled]: (state, action) => ({
        ...state,
        isLoading: false,
        locationSpendList: getOr([], 'payload', action),
      }),
      [getLocationSpendList.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
      [changeLoyaltyProgram.pending]: (state) => ({
        ...state,
        isLoading: true,
      }),
      [changeLoyaltyProgram.fulfilled]: (state) => ({
        ...state,
        isLoading: false,
      }),
      [changeLoyaltyProgram.rejected]: (state, payload) => ({
        ...state,
        isLoading: false,
        error: payload,
      }),
    },
  }),
};

const selectors = {
  selectLoyaltyProgram: (state) => getOr([], 'program', state[name]),
  editLoyaltyModal: (state) => getOr([], 'editProgram', state[name]),
  selectIsLoading: (state) => getOr({}, 'isLoading', state[name]),
  selectItems: (state) => getOr([], 'items', state[name]),
  selectProgramList: (state) => getOr({}, 'programs', state[name]),
  selectLoyaltyList: (state) => getOr({}, 'loyalties', state[name]),
  selectCsv: (state) => getOr({}, 'csv', state[name]),
  selectLoyaltyTransactions: (state) =>
    getOr({ data: [], total: 0 }, 'loyaltyTransactions', state[name]),
  selectIsSending: (state) => getOr(false, 'isSending', state[name]),
  selectProgramListById: (state) => getOr(false, 'programsById', state[name]),
  selectOverarchingPrograms: (state) =>
    getOr({ data: [], total: 0 }, 'overarchingPrograms', state[name]),
  selectTopCustomers: (state) =>
    getOr([], 'listCustomerEarnPoint', state[name]),
  selectReferredCustomers: (state) =>
    getOr([], 'referredCustomers', state[name]),
  selectSpendStats: (state) => getOr([], 'spendStats', state[name]),
  selectSpendList: (state) => getOr([], 'spendList', state[name]),
  selectSecondarySpendList: (state) =>
    getOr([], 'secondarySpendList', state[name]),
  selectLocationSpendList: (state) =>
    getOr([], 'locationSpendList', state[name]),
};

export default {
  actions: {
    ...actions,
    saveLoyaltyProgram,
    editLoyaltyProgram,
    setEditProgram,
    getNewLoyaltyProgram,
    downloadLoyaltyCSV,
    uploadLoyaltyCSV,
    getProgramList,
    enableOverarchingProgram,
    getOverarchingProgramList,
    getLoyaltyList,
    getItems,
    getItemsByCategory,
    getLoyaltyTransactions,
    resendEmail,
    sendReferralEmail,
    sendWhatsApp,
    getLoyaltyByProgram,
    sendCustomEmail,
    getTopCustomerEarnPoint,
    getReferredCustomers,
    getSpendStats,
    getSpendList,
    getBusinessSpendList,
    getLocationSpendList,
    changeLoyaltyProgram,
  },
  selectors,
  reducer,
  name,
};
